<script lang="ts">
  /** Free text label. Double-click to edit in place. */
  import { NodeResizer, useSvelteFlow, type NodeProps } from '@xyflow/svelte';
  import type { BoardItemStyle } from '../../lib/types';
  import { getBoardActions } from '../../lib/boardContext';

  type Props = NodeProps & {
    data: {
      content: string;
      style: BoardItemStyle;
    };
  };

  let { id, data, selected, width, height }: Props = $props();

  const { updateNodeData } = useSvelteFlow();
  const actions = getBoardActions();

  let editing = $state(false);
  let inputEl = $state<HTMLTextAreaElement | undefined>();

  let style = $derived(data.style ?? {});

  function startEditing() {
    actions.onContentStart?.(id);
    editing = true;
  }

  function stopEditing() {
    editing = false;
    actions.onContentChange(id, data.content);
  }

  function handleInput(event: Event) {
    const value = (event.currentTarget as HTMLTextAreaElement).value;
    updateNodeData(id, { content: value });
  }

  function handleKeydown(event: KeyboardEvent) {
    // Escape commits and exits; Enter stays available for multi-line labels.
    if (event.key === 'Escape') {
      event.preventDefault();
      stopEditing();
    }
  }

  // Focus the textarea as soon as it enters the DOM.
  // eslint-disable-next-line momentium/no-unguarded-effect -- DOM focus management, guarded on editing state
  $effect(() => {
    if (editing && inputEl) {
      inputEl.focus();
      inputEl.select();
    }
  });
</script>

<NodeResizer
  isVisible={selected}
  minWidth={60}
  minHeight={28}
  color="#2563eb"
  onResizeStart={() => actions.onResizeStart?.(id)}
  onResizeEnd={(_event, params) => actions.onResizeEnd(id, params)}
/>

<!-- svelte-ignore a11y_no_static_element_interactions -->
<div
  class="text-node"
  class:selected
  ondblclick={startEditing}
  style="
    width: {width ? `${width}px` : 'auto'};
    height: {height ? `${height}px` : 'auto'};
    color: {style.textColor ?? '#1f2937'};
    background: {style.fill ?? 'transparent'};
    border-color: {style.stroke ?? 'transparent'};
    font-size: {style.fontSize ?? '16'}px;
    font-weight: {style.fontWeight ?? '500'};
    font-style: {style.fontStyle ?? 'normal'};
    text-align: {style.textAlign ?? 'left'};
    justify-content: {style.verticalAlign ?? 'center'};
  "
>
  {#if editing}
    <textarea
      bind:this={inputEl}
      class="text-input nodrag nowheel"
      value={data.content}
      oninput={handleInput}
      onblur={stopEditing}
      onkeydown={handleKeydown}></textarea>
  {:else}
    <span class="text-display">{data.content || 'Double-click to edit'}</span>
  {/if}
</div>

<style>
  .text-node {
    display: flex;
    flex-direction: column;
    min-width: 60px;
    min-height: 28px;
    padding: 0.25rem 0.375rem;
    border: 1px solid transparent;
    border-radius: 0.25rem;
    box-sizing: border-box;
    line-height: 1.35;
    white-space: pre-wrap;
    word-break: break-word;
    cursor: grab;
  }

  .text-node.selected {
    outline: 2px solid #2563eb;
    outline-offset: 2px;
  }

  .text-display {
    display: block;
    width: 100%;
  }

  .text-input {
    width: 100%;
    height: 100%;
    min-height: 1.35em;
    border: none;
    outline: none;
    padding: 0;
    margin: 0;
    background: transparent;
    resize: none;
    font: inherit;
    color: inherit;
    text-align: inherit;
    line-height: inherit;
  }
</style>
